import type { InfiniteData } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { MessagingChannel } from '~hooks/useMessaging/models';
import useSendMessage from '~hooks/useMessaging/useSendMessage';
import { MESSAGING_QUERY_KEY } from '~queries/messagingQueryKeys';
import type { MessagingMessagesPage } from '~queries/useMessagingMessagesInfiniteQuery';
import { Nullable } from '~types/Nullable';

/**
 * What a pending row needs to draw a chip, plus the signed id the send will carry. The
 * composer's object-URL previews are deliberately left behind: they are revoked as soon
 * as the composer clears, which happens before this row first renders.
 */
export type MessagingOutboxAttachment = {
  filename: string;
  isImage: boolean;
  signedId: string;
};

export type MessagingOutboxEntry = {
  attachments: MessagingOutboxAttachment[];
  clientMessageId: string;
  content: string;
  createdAt: Dayjs;
  hasFailed: boolean;
  /** Held on the entry so a retry re-sends the reply's broadcast, not a bare message. */
  repliedToBroadcastChannelId?: string;
};

type MessagingOutboxContextValue = {
  entriesByChannelId: Record<string, MessagingOutboxEntry[]>;
  retrySend: (
    channel: MessagingChannel,
    clientMessageId: string,
  ) => Promise<Nullable<string>>;
  sendThroughOutbox: (input: {
    attachments: MessagingOutboxAttachment[];
    channel: MessagingChannel;
    content: string;
    repliedToBroadcastChannelId?: string;
  }) => Promise<Nullable<string>>;
};

const MessagingOutboxContext = createContext<MessagingOutboxContextValue | undefined>(
  undefined,
);

// Unsent messages live outside the react-query cache, which a refetch replaces wholesale.
export const MessagingOutboxProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const { sendMessage } = useSendMessage();

  const [entriesByChannelId, setEntriesByChannelId] = useState<
    Record<string, MessagingOutboxEntry[]>
  >({});

  const appendEntry = useCallback(
    (channelId: string, entry: MessagingOutboxEntry) =>
      setEntriesByChannelId((existing) => ({
        ...existing,
        [channelId]: [...(existing[channelId] ?? []), entry],
      })),
    [],
  );

  const removeEntry = useCallback(
    (channelId: string, clientMessageId: string) =>
      setEntriesByChannelId((existing) => ({
        ...existing,
        [channelId]: (existing[channelId] ?? []).filter(
          (entry) => entry.clientMessageId !== clientMessageId,
        ),
      })),
    [],
  );

  const setEntryFailed = useCallback(
    (channelId: string, clientMessageId: string, hasFailed: boolean) =>
      setEntriesByChannelId((existing) => ({
        ...existing,
        [channelId]: (existing[channelId] ?? []).map((entry) =>
          entry.clientMessageId === clientMessageId ? { ...entry, hasFailed } : entry,
        ),
      })),
    [],
  );

  // Every transcript, not just the one posted from: a `reply_to_sender` send is routed
  // into another channel, and the echo lands wherever it went.
  const isShownAsSent = useCallback(
    (clientMessageId: string) =>
      queryClient
        .getQueriesData<InfiniteData<MessagingMessagesPage>>({
          queryKey: [MESSAGING_QUERY_KEY, 'messages'],
        })
        .some(([, transcript]) =>
          transcript?.pages.some((page) =>
            page.messages.some((message) => message.clientMessageId === clientMessageId),
          ),
        ),
    [queryClient],
  );

  const postEntry = useCallback(
    async (channel: MessagingChannel, entry: MessagingOutboxEntry) => {
      try {
        const sent = await sendMessage({
          channel,
          clientMessageId: entry.clientMessageId,
          content: entry.content,
          fileSignedIds: entry.attachments.map((attachment) => attachment.signedId),
          repliedToBroadcastChannelId: entry.repliedToBroadcastChannelId,
        });

        removeEntry(channel.id, entry.clientMessageId);
        return sent.channelId;
      } catch {
        // An echo that beat the failing response means the message landed anyway.
        if (isShownAsSent(entry.clientMessageId)) {
          removeEntry(channel.id, entry.clientMessageId);
        } else {
          setEntryFailed(channel.id, entry.clientMessageId, true);
        }

        return null;
      }
    },
    [isShownAsSent, removeEntry, sendMessage, setEntryFailed],
  );

  const hasEntries = Object.values(entriesByChannelId).some(
    (entries) => entries.length > 0,
  );

  // The cached pages proving a parked entry landed turn over, so it has to be dropped
  // while that proof is still there, or a later load resurrects the Retry.
  useEffect(() => {
    if (!hasEntries) return;

    return queryClient.getQueryCache().subscribe(() => {
      setEntriesByChannelId((existing) => {
        const reconciled = Object.fromEntries(
          Object.entries(existing).map(([channelId, entries]) => [
            channelId,
            entries.filter((entry) => !isShownAsSent(entry.clientMessageId)),
          ]),
        );
        const hasDrop = Object.entries(reconciled).some(
          ([channelId, entries]) => entries.length !== existing[channelId].length,
        );

        return hasDrop ? reconciled : existing;
      });
    });
  }, [hasEntries, isShownAsSent, queryClient]);

  const sendThroughOutbox = useCallback(
    ({
      attachments,
      channel,
      content,
      repliedToBroadcastChannelId,
    }: {
      attachments: MessagingOutboxAttachment[];
      channel: MessagingChannel;
      content: string;
      repliedToBroadcastChannelId?: string;
    }) => {
      const entry: MessagingOutboxEntry = {
        attachments,
        clientMessageId: crypto.randomUUID(),
        content,
        createdAt: dayjs(),
        hasFailed: false,
        repliedToBroadcastChannelId,
      };

      appendEntry(channel.id, entry);
      return postEntry(channel, entry);
    },
    [appendEntry, postEntry],
  );

  /** Reuses the entry's client message id, so the api dedupes a retry that raced. */
  const retrySend = useCallback(
    (channel: MessagingChannel, clientMessageId: string) => {
      const entry = (entriesByChannelId[channel.id] ?? []).find(
        (candidate) => candidate.clientMessageId === clientMessageId,
      );
      if (!entry) return Promise.resolve(null);

      setEntryFailed(channel.id, clientMessageId, false);
      return postEntry(channel, entry);
    },
    [entriesByChannelId, postEntry, setEntryFailed],
  );

  return (
    <MessagingOutboxContext.Provider
      value={{ entriesByChannelId, retrySend, sendThroughOutbox }}
    >
      {children}
    </MessagingOutboxContext.Provider>
  );
};

export const useMessagingOutbox = () => {
  const context = useContext(MessagingOutboxContext);

  if (!context) {
    throw new Error('useMessagingOutbox requires a MessagingOutboxProvider');
  }

  return context;
};
